// External Packages
import { useState, useEffect } from "react";
import { MdRefresh } from "react-icons/md";

// Internal Packages
import Heading from "../UI/Heading";
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import Transfers from "./Transfers";
import { endOfDay, startOfDay } from "date-fns";
import { isoDateToString } from "../../util/format-date";
import { arrayToCsv } from "../../util/arrayToCsv";
import { TbFileTypeCsv } from "react-icons/tb";

/** ========================================================================
 * * Payments Report
 * Renders a page to view payment targets, statistics, and action items.
 * ========================================================================== */

export default function PaymentsReport() {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({});

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      // Get Report Data
      const response = await requestAdminAPI("get-cash-report", {
        startOfDay: startOfDay(new Date()).toISOString(),
        endOfDay: endOfDay(new Date()).toISOString(),
      });
      setReport(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Get Bar Classes
  const classNames = () => {
    let classes = `flex transition-all justify-center items-center px-2 bg-yellow-600 h-10 rounded-l-md`;
    let ratio =
      (Number(report.todayIncome) / Number(report.pendingExpenses)) * 100;
    if (ratio > 100) ratio = 100;
    return classes + ` w-[${ratio.toFixed(0)}%]`;
  };

  /* ========================= DOWNLOAD CASH BOOK ======================= */
  const downloadCashBook = async () => {
    try {
      setLoading(true);
      const cash = await requestAdminAPI("filter", {
        table: "payment",
        filters: [
          `status: { eq: "succeeded" }`,
          `paidVia: { eq: "_cash" }`,
          `datePaid: { ge: "2025-07-21T05:00:00.000Z" }`,
        ],
        columns: ["customer { firstName, lastName }"],
      });
      const expenses = await requestAdminAPI("filter", {
        table: "expense",
        filters: [
          `status: { eq: "succeeded" }`,
          `paidVia: { eq: "_cash" }`,
          `datePaid: { ge: "2025-07-21T05:00:00.000Z" }`,
        ],
        columns: ["supplier { name }"],
      });
      const toTransfers = await requestAdminAPI("filter", {
        table: "transfer",
        filters: [
          `to: { eq: "_cash" }`,
          `dateCreated: { ge: "2025-07-21T05:00:00.000Z" }`,
        ],
      });
      const fromTransfers = await requestAdminAPI("filter", {
        table: "transfer",
        filters: [
          `from: { eq: "_cash" }`,
          `dateCreated: { ge: "2025-07-21T05:00:00.000Z" }`,
        ],
      });

      const data = [];
      // Format Cash Payments
      cash.forEach((payment) => {
        const entry = {
          "Record ID": payment.id,
          Type: "Payment",
          Date: isoDateToString(payment.datePaid),
          Account: payment.category,
          Description: payment.description || "",
          "Supplier/Client": `${payment.customer.firstName} ${payment.customer.lastName}`,
          "Cash In": payment.amount,
          "Cash Out": "",
        };
        data.push(entry);
      });

      // Format Cash Expenses
      expenses.forEach((expense) => {
        const entry = {
          "Record ID": expense.id,
          Type: "Expense",
          Date: isoDateToString(expense.datePaid),
          Account: expense.category,
          Description: expense.description || "",
          "Supplier/Client": expense.supplier?.name || "",
          "Cash In": "",
          "Cash Out": expense.amount,
        };
        data.push(entry);
      });

      // Format Cash Transfers In
      toTransfers.forEach((transfer) => {
        const entry = {
          "Record ID": transfer.id,
          Type: "Transfer In",
          Date: isoDateToString(transfer.dateCreated),
          Account: "",
          Description: transfer.reason || "",
          "Supplier/Client":
            transfer.from === "_zelle" ? "Chase Bank" : "PNC Bank",
          "Cash In": transfer.amount,
          "Cash Out": "",
        };
        data.push(entry);
      });

      // Format Cash Transfers Out
      fromTransfers.forEach((transfer) => {
        const entry = {
          "Record ID": transfer.id,
          Type: "Transfer Out",
          Date: isoDateToString(transfer.dateCreated),
          Account: "",
          Description: transfer.reason || "",
          "Supplier/Client":
            transfer.to === "_zelle" ? "Chase Bank" : "PNC Bank",
          "Cash In": "",
          "Cash Out": transfer.amount,
        };
        data.push(entry);
      });

      // Sort Data by Date Oldest to Newest
      data.sort((a, b) => new Date(a.Date) - new Date(b.Date));

      // Calculate Balance
      let balance = 650;
      data.forEach((entry) => {
        if (entry["Cash In"]) balance += Number(entry["Cash In"]);
        if (entry["Cash Out"]) balance -= Number(entry["Cash Out"]);
        entry.Balance = balance.toFixed(2);
      });

      // To CSV
      arrayToCsv(
        [
          {
            "Record ID": "",
            Type: "Opening Balance",
            Date: "07/21/2025",
            Account: "",
            Description: "",
            "Supplier/Client": "",
            "Cash In": "650.00",
            "Cash Out": "",
            Balance: "650.00",
          },
          ...data,
        ],
        "cash-book.csv"
      );
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, []);

  return (
    <div className="container grid gap-6">
      <Heading title="Payments Report" isBackArrow={true}>
        <TbFileTypeCsv
          className="icon"
          title="Download Cash Book"
          onClick={downloadCashBook}
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl icon"
          title="Refresh"
        />
      </Heading>

      <div className="grid gap-1 text-sm">
        <p className="font-semibold text-stone-200">TARGET</p>
        <div className="flex items-center justify-between w-full space-x-4">
          {loading ? (
            <div className="w-full py-5 border-2 rounded-md bg-stone-700 border-stone-700 animate-pulse"></div>
          ) : (
            <div className="flex items-center w-full border-2 rounded-md bg-stone-700 border-stone-700">
              <div className={classNames()}>
                <p className="font-semibold">${report.todayIncome}</p>
              </div>
              <p className="mx-auto font-semibold">
                {Number(report.pendingExpenses) - Number(report.todayIncome) > 0
                  ? `$${(
                      Number(report.pendingExpenses) -
                      Number(report.todayIncome)
                    ).toFixed(2)}`
                  : ""}
              </p>
            </div>
          )}
          {loading ? (
            <div className="w-40 h-full rounded-lg animate-pulse bg-stone-600"></div>
          ) : (
            <p className="text-3xl font-semibold text-stone-200">
              ${report.pendingExpenses}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6 text-sm">
        <div className="grid col-span-2 gap-4 text-sm">
          <div className="grid items-end grid-cols-3 gap-2">
            <DetailPair
              label="Cash Balance"
              classes="text-2xl font-bold text-sky-500"
              value={`$${report.cashBalance}`}
              loading={loading}
            />
            <DetailPair
              label="Previous Balance"
              classes="text-base font-semibold text-sky-600"
              value={`$${report.previousBalance}`}
              loading={loading}
            />
            <DetailPair
              label="Scoped Balance"
              classes="text-base font-semibold text-sky-600"
              value={`$${report.adminBalance}`}
              loading={loading}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <DetailPair
              label="Cash Income"
              classes="text-base font-semibold text-green-500"
              value={`$${report.cashIncome}`}
              loading={loading}
            />
            <DetailPair
              label="Cash Expenses"
              classes="text-base font-semibold text-red-500"
              value={`$${report.cashExpenses}`}
              loading={loading}
            />
            <DetailPair
              label="Cash Deposits"
              classes="text-base font-semibold text-stone-400"
              value={`$${report.cashTransfers}`}
              loading={loading}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <DetailPair
              label="Scoped Cash Income"
              classes="text-base font-semibold text-green-600"
              value={`$${report.adminIncome}`}
              loading={loading}
            />
            <DetailPair
              label="Scoped Expenses"
              classes="text-base font-semibold text-red-600"
              value={`$${report.adminExpenses}`}
              loading={loading}
            />
            <DetailPair
              label="Scoped Deposits"
              classes="text-base font-semibold text-stone-500"
              value={`$${report.adminTransfers}`}
              loading={loading}
            />
          </div>
        </div>
        <div className="grid content-start gap-4 text-sm">
          <DetailPair
            label="Expenses Due Today"
            classes="text-2xl font-semibold text-red-600"
            value={`$${report.dueTodayExpenses}`}
            loading={loading}
          />
          <DetailPair
            label="Total Pending Expenses"
            classes="text-base font-semibold text-red-500"
            value={`$${report.pendingExpenses}`}
            loading={loading}
          />
        </div>
        <div className="grid content-start gap-4 text-sm">
          <DetailPair
            label="Payments Due Today"
            classes="text-2xl font-bold text-orange-600"
            value={`$${report.dueTodayPayments}`}
            loading={loading}
          />
          <DetailPair
            label="Total Pending Payments"
            classes="text-base font-semibold text-orange-500"
            value={`$${report.pendingPayments}`}
            loading={loading}
          />
        </div>
      </div>

      <div className="mt-6">
        <Transfers refresh={fetchRecords} />
      </div>
    </div>
  );
}
